/**
 * Inspector.tsx — what is selected, as the Engine resolved it.
 *
 * The port list is the part that matters. It shows each port's resolved kind
 * and its DECLARED flow direction, and when a port has no declared direction it
 * says "not declared" rather than guessing from which side of the body the port
 * sits on. Geometry never implies flow: `right_of` is not `downstream`, and a
 * port on the right is not an outlet.
 */

import { Empty, Panel } from "../../components/Panel";
import type { Hit } from "../../canvas/hitTest";

export function Inspector({ hit, onRotate, onRemove, onInsertInline, readOnly }: {
  hit: Hit;
  onRotate?: (symbolId: string) => void;
  onRemove?: (symbolId: string) => void;
  onInsertInline?: (traceId: string) => void;
  readOnly?: boolean;
}): JSX.Element {
  if (hit.kind === "none") {
    return <Panel title="Selection"><Empty>Nothing selected.</Empty></Panel>;
  }

  if (hit.kind === "trace") {
    const trace = hit.trace;
    return (
      <Panel
        title="Trace"
        actions={!readOnly && onInsertInline ? <button type="button" onClick={() => onInsertInline(trace.id)}>Insert inline…</button> : undefined}
      >
        <dl className="facts">
          <dt>Id</dt><dd><code>{trace.id}</code></dd>
          <dt>Domain</dt><dd>{trace.traceDomain}</dd>
          <dt>Kind</dt><dd>{trace.traceKind}</dd>
          <dt>Line width</dt><dd>{trace.lineWidthMm} mm (DIN)</dd>
          {trace.lineNumber && <><dt>Line number</dt><dd>{trace.lineNumber}</dd></>}
          {trace.fluid && <><dt>Fluid</dt><dd>{trace.fluid}</dd></>}
          {trace.DN !== undefined && <><dt>DN</dt><dd>{trace.DN}</dd></>}
          {trace.PN !== undefined && <><dt>PN</dt><dd>{trace.PN}</dd></>}
          {trace.cableName && <><dt>Cable</dt><dd>{trace.cableName}</dd></>}
          <dt>Endpoints</dt>
          <dd><code>{trace.source.symbolId}:{trace.source.portId}</code> ↔ <code>{trace.target.symbolId}:{trace.target.portId}</code></dd>
          <dt>Route</dt><dd>{trace.path.length} points{trace.routeStatus ? ` · ${trace.routeStatus}` : ""}</dd>
        </dl>
        <p className="muted">
          The stored point order is not flow direction. Flow comes from the ports&apos; declared roles.
        </p>
      </Panel>
    );
  }

  const symbol = hit.kind === "port" ? hit.symbol : hit.symbol;
  return (
    <Panel
      title={symbol.defName ?? symbol.defId}
      actions={!readOnly ? (
        <>
          {onRotate && <button type="button" className="ghost" onClick={() => onRotate(symbol.id)}>Rotate 90°</button>}
          {onRemove && <button type="button" className="ghost" onClick={() => onRemove(symbol.id)}>Remove</button>}
        </>
      ) : undefined}
    >
      <dl className="facts">
        <dt>Instance</dt><dd><code>{symbol.id}</code></dd>
        <dt>Definition</dt><dd><code>{symbol.defId}</code></dd>
        {symbol.objectId && <><dt>Object</dt><dd><code>{symbol.objectId}</code></dd></>}
        <dt>Origin</dt><dd>{symbol.origin.x} , {symbol.origin.y} mm</dd>
        <dt>Rotation</dt><dd>{symbol.rotation}°</dd>
        <dt>Extent</dt><dd>{round(symbol.bbox.w)} × {round(symbol.bbox.h)} mm</dd>
      </dl>

      <h3>Ports</h3>
      {symbol.ports.length === 0 ? <Empty>This definition declares no ports.</Empty> : (
        <table className="ports">
          <thead>
            <tr><th>Port</th><th>Kind</th><th>Flow</th><th>Dir</th><th /></tr>
          </thead>
          <tbody>
            {symbol.ports.map(port => (
              <tr key={port.id} className={hit.kind === "port" && hit.port.id === port.id ? "is-selected" : undefined}>
                <td><code>{port.id}</code>{port.label ? <span className="muted"> {port.label}</span> : null}</td>
                <td>{port.kindFamily}{port.kindSubfamily ? ` / ${port.kindSubfamily}` : ""}</td>
                <td className={port.flowDirection ? undefined : "muted"}>{port.flowDirection ?? "not declared"}</td>
                <td>{port.dir}</td>
                <td>{port.connected ? "connected" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}
